import { Icon } from '../../../components/icons/Icon'
import { activeChips, type DashboardFilterAction, type DashboardFilterState } from './dashboardFilterReducer'
import type { DashboardFilter } from '../../../types/insights'

export interface FilterChipBarProps {
  state: DashboardFilterState
  dispatch: (action: DashboardFilterAction) => void
  /** axis -> human label, e.g. { jurisdiction: 'Jurisdiction' }. Falls back to the raw axis name. */
  axisLabels?: Record<string, string>
}

/** Every active filter/cross-filter/territory/slider as a removable chip, plus "Clear all" (AC10/11). */
export function FilterChipBar({ state, dispatch, axisLabels = {} }: FilterChipBarProps) {
  const chips = activeChips(state)
  if (chips.length === 0) return null

  function labelFor(axis: string): string {
    return axisLabels[axis] ?? axis
  }

  function remove(chip: ReturnType<typeof activeChips>[number]) {
    if (chip.kind === 'cross-filter') dispatch({ type: 'REMOVE_CROSSFILTER', axis: chip.axis as keyof DashboardFilter })
    else if (chip.kind === 'territory') dispatch({ type: 'SET_TERRITORY', territory: undefined })
    else if (chip.kind === 'slider') dispatch({ type: 'REMOVE_SLIDER', axis: chip.axis })
    else dispatch({ type: 'SET_FILTER', axis: chip.axis as keyof DashboardFilter, value: undefined })
  }

  return (
    <div className="iw-filter-chip-bar" role="group" aria-label="Active dashboard filters">
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          className="iw-chip"
          aria-pressed="true"
          onClick={() => remove(chip)}
          aria-label={`Remove filter ${labelFor(chip.axis)}: ${chip.value}`}
        >
          {labelFor(chip.axis)}: {chip.value}
          <Icon name="x" size={12} />
        </button>
      ))}
      <button type="button" className="iw-btn iw-btn--sm iw-btn--ghost" onClick={() => dispatch({ type: 'CLEAR_ALL' })}>
        Clear all
      </button>
    </div>
  )
}
